import {
  AlertTriangle,
  Ban,
  Banknote,
  CalendarClock,
  CalendarRange,
  Contact,
  Factory,
  DoorClosed,
  DoorOpen,
  FileMinus2,
  FileText,
  FileX2,
  Handshake,
  Gavel,
  Home,
  KeyRound,
  ListChecks,
  LogIn,
  LogOut,
  MapPinned,
  MapPinOff,
  Percent,
  Plug,
  Printer,
  Receipt,
  RefreshCw,
  Scale,
  Sparkles,
  Send,
  Settings2,
  ShieldOff,
  Tags,
  Undo2,
  Upload,
  UserCog,
  UserRoundCog,
  type LucideIcon,
} from 'lucide-react';

export interface Rotulo {
  texto: string;
  icono: LucideIcon;
  tono: string;
}

const GRIS = 'bg-armazon-100 text-armazon-500';
const ROJO = 'bg-red-50 text-red-600';
const AMBAR = 'bg-amber-50 text-amber-600';
const VERDE = 'bg-marca-50 text-marca-600';
const AZUL = 'bg-blue-50 text-blue-600';

/**
 * Como se lee cada accion en la pantalla.
 *
 * Tiene que estar COMPLETO: lo que falte aqui sale con su codigo crudo
 * --`cambiar_usuario`--, que es un identificador de la base y no una frase. Si se añade
 * una accion en `Auditoria.Accion`, se añade aqui el mismo dia.
 */
export const ACCIONES: Record<string, Rotulo> = {
  entrar: { texto: 'Entró', icono: LogIn, tono: GRIS },
  salir: { texto: 'Salió', icono: LogOut, tono: GRIS },
  entrada_fallida: { texto: 'Intento fallido', icono: ShieldOff, tono: ROJO },

  cobrar: { texto: 'Cobró', icono: Banknote, tono: VERDE },
  anular_cobro: { texto: 'Anuló un cobro', icono: Ban, tono: ROJO },
  adelantar: { texto: 'Adelantó meses', icono: CalendarClock, tono: VERDE },
  perdonar_mora: { texto: 'Cobró sin mora', icono: Percent, tono: ROJO },
  abrir_caja: { texto: 'Abrió la caja', icono: DoorOpen, tono: VERDE },
  cerrar_caja: { texto: 'Cerró la caja', icono: DoorClosed, tono: AZUL },

  emitir_lote: { texto: 'Emitió al SRI', icono: Send, tono: AZUL },
  anular_factura: { texto: 'Anuló una factura', icono: FileX2, tono: ROJO },
  acreditar: { texto: 'Acreditó', icono: FileMinus2, tono: AMBAR },

  exonerar: { texto: 'Exoneró', icono: AlertTriangle, tono: AMBAR },
  cambiar_tarifa: { texto: 'Cambió una tarifa', icono: Tags, tono: AMBAR },
  cambiar_tasa_mora: { texto: 'Cambió la mora', icono: Percent, tono: ROJO },
  cambiar_catalogo: { texto: 'Cambió el catálogo tarifario', icono: Tags, tono: AMBAR },

  cambiar_generador: { texto: 'Cambió un generador', icono: Factory, tono: AMBAR },
  cambiar_ciclo: { texto: 'Abrió o cerró un ciclo', icono: CalendarRange, tono: AZUL },
  cambiar_pesaje: { texto: 'Cambió un pesaje', icono: Scale, tono: AMBAR },
  importar_pesajes: { texto: 'Importó pesajes', icono: Upload, tono: AZUL },
  cambiar_cargo: { texto: 'Cambió un cargo puntual', icono: Sparkles, tono: AMBAR },
  facturar_generadores: { texto: 'Facturó servicios especiales', icono: Receipt, tono: AZUL },

  cambiar_retencion: { texto: 'Cambió una retención', icono: FileText, tono: AMBAR },

  cambiar_cliente: { texto: 'Cambió un cliente', icono: Contact, tono: AMBAR },
  cambiar_cuenta: { texto: 'Cambió un predio', icono: Home, tono: AMBAR },
  dar_de_baja: { texto: 'Dio de baja un predio', icono: MapPinOff, tono: ROJO },
  traspasar_cuenta: { texto: 'Traspasó un predio', icono: UserCog, tono: AMBAR },
  cambiar_sector: { texto: 'Cambió una parroquia', icono: MapPinned, tono: AMBAR },
  importar_catastro: { texto: 'Importó catastro', icono: Upload, tono: AZUL },

  cambiar_usuario: { texto: 'Cambió un usuario', icono: UserRoundCog, tono: AMBAR },
  cambiar_permisos: { texto: 'Cambió permisos', icono: KeyRound, tono: AMBAR },
  cambiar_configuracion: { texto: 'Cambió la configuración', icono: Settings2, tono: AMBAR },
  cambiar_punto_emision: { texto: 'Cambió un punto de emisión', icono: Printer, tono: ROJO },

  alta_de_consumidor: { texto: 'Dio de alta un consumidor', icono: Plug, tono: AZUL },
  cambiar_consumidor: { texto: 'Cambió un consumidor', icono: Plug, tono: AMBAR },
  renovar_credenciales: { texto: 'Renovó credenciales', icono: RefreshCw, tono: ROJO },

  extinguir_titulos: { texto: 'Extinguió títulos', icono: Gavel, tono: ROJO },
  revertir_extincion: { texto: 'Revirtió una extinción', icono: Undo2, tono: AMBAR },
  regularizar_titulos: { texto: 'Regularizó títulos', icono: ListChecks, tono: AZUL },
  registrar_convenio: { texto: 'Registró un convenio', icono: Handshake, tono: VERDE },
  anular_convenio: { texto: 'Anuló un convenio', icono: Ban, tono: ROJO },
  adjuntar_documento: { texto: 'Adjuntó un documento', icono: Upload, tono: AZUL },
  quitar_documento: { texto: 'Quitó un documento', icono: FileX2, tono: AMBAR },
  emitir_titulo_de_credito: { texto: 'Emitió un título de crédito', icono: FileText, tono: AZUL },
  completar_convenio: { texto: 'Completó los datos de un convenio', icono: Handshake, tono: AMBAR },
  aprobar_convenio: { texto: 'Aprobó un convenio', icono: Handshake, tono: AMBAR },
  rechazar_convenio: { texto: 'Rechazó un convenio', icono: Handshake, tono: AMBAR },
  incumplir_convenio: { texto: 'Dio un convenio por incumplido', icono: Handshake, tono: AMBAR },
};

export const MODULOS: Record<string, string> = {
  acceso: 'Acceso',
  recaudacion: 'Recaudación',
  facturacion: 'Facturación',
  catastro: 'Catastro',
  configuracion: 'Configuración',
  identidad: 'Identidad',
  api_publica: 'API pública',
  titulos: 'Títulos',
  generadores: 'Servicios especiales',
  compras: 'Compras',
  convenios: 'Convenios',
};

export function accionDe(codigo: string): Rotulo {
  return ACCIONES[codigo] ?? { texto: codigo, icono: AlertTriangle, tono: GRIS };
}

export function moduloDe(codigo: string): string {
  return MODULOS[codigo] ?? codigo;
}
